import React, { useEffect, useMemo } from 'react';
import TextareaAutosize, {
  TextareaAutosizeProps,
} from '@mui/material/TextareaAutosize';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import palette from '@ui/palette';
import Typography from '@ui/Typography';
import useSRAnnouncer from '../../lib/useSRAnnouncer';

interface TextAreaProps extends Omit<TextareaAutosizeProps, 'onChange'> {
  error?: boolean;
  errorMessage?: string;
  maxLength?: number;
  onChange: (value: string) => void;
  outlineColor?: string;
  showCharacterCount?: boolean;
}

const useStyles = makeStyles<{ error: boolean; outlineColor?: string }>()(
  (theme, { error, outlineColor }) => ({
    root: {
      resize: 'vertical',
      backgroundColor: 'unset',
      border: 'none',
      outline: `1px solid ${
        error ? theme.palette.error.light : palette.gray_75
      }`,
      borderRadius: theme.spacing(0.5),
      color: palette.gray_75,
      fontFamily: theme.typography.fontFamily,
      padding: theme.spacing(1),
      minWidth: theme.spacing(24.625),
      minHeight: theme.spacing(8),
      '&:focus-visible': {
        outline: `1px solid ${outlineColor || theme.palette.primary.main}`,
        color: theme.palette.grey[50],
      },
      '&[disabled]': {
        cursor: 'not-allowed',
      },
    },
  })
);

const TextArea = (props: TextAreaProps) => {
  const {
    error,
    errorMessage,
    onChange,
    'aria-label': ariaLabel,
    maxLength = 500,
    value,
    outlineColor,
    showCharacterCount = true,
    className,
    ...rest
  } = props;
  const { announce } = useSRAnnouncer();
  const isLimitReached = useMemo(() => {
    return (value?.toString().length ?? 0) === maxLength;
  }, [maxLength, value]);
  const { classes } = useStyles({ error: Boolean(error), outlineColor });

  useEffect(() => {
    if (isLimitReached) {
      announce(`Max character count ${maxLength} reached`);
    }
  }, [announce, isLimitReached, maxLength]);

  const formattedCharacterCount = `${
    value?.toString().length ?? 0
  }/${maxLength}`;
  return (
    <Box display={'flex'} flexDirection={'column'}>
      <TextareaAutosize
        {...rest}
        value={value}
        onChange={event => onChange(event.target.value)}
        className={clsx(classes.root, className)}
        aria-label={ariaLabel}
        maxLength={maxLength}
      />
      {showCharacterCount && (
        <Box pt={1}>
          <Typography variant="pMd" color="tertiary">
            {formattedCharacterCount}
          </Typography>
        </Box>
      )}
      {error && errorMessage && (
        <Typography
          variant="pMd"
          color="error"
          role="alert"
          aria-label={ariaLabel}
        >
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default TextArea;
